import { useState, useEffect } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { fmt } from '../../utils/format';
import { useApi } from '../../hooks/useApi';
import { US_STATES, fetchCountiesForState } from '../../constants/geography';
import { Btn, Input, Select, Spinner, useToast, Modal, ModalFooter, Icon } from '../ui';

export const EMPTY_PARCEL_FORM = { parcelNumber: '', taxParcelNumber: '', regridParcelId: '', state: '', county: '', ppin: '', acres: '', legalDescription: '', notes: '', purchasedDate: '', soldDate: '', purchasePrice: '', salePrice: '' };

// ---------------------------------------------------------------------------
// ParcelsList — the parcel records for an entity (add / edit / enrich /
// combine / move / delete). The map and all spatial tools live in the
// separate Map tab (ParcelsMap in ParcelsMapTab.jsx).
// ---------------------------------------------------------------------------
export const ParcelsList = ({ propertyID, loanID, dealDetailId, dealId }) => {
  const { toast } = useToast();
  // dealId scopes to every parcel across a whole deal (Farm/land module). In
  // that mode there's no single detail to add/combine into, so those actions
  // are hidden — the list is read + per-parcel edit/enrich/move/delete.
  const entityParam = dealDetailId ? `dealDetailId=${dealDetailId}`
                    : dealId       ? `dealId=${dealId}`
                    : propertyID   ? `propertyId=${propertyID}`
                    : `loanId=${loanID}`;
  const { data: parcels, loading, reload } = useApi(() => api.get(`parcels?${entityParam}`), [propertyID, loanID, dealDetailId, dealId]);

  const [enriching,    setEnriching]    = useState(null);
  const [addForm,      setAddForm]      = useState(EMPTY_PARCEL_FORM);
  const [addCounties,  setAddCounties]  = useState([]);
  const [showAdd,      setShowAdd]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [editingId,    setEditingId]    = useState(null);
  const [editForm,     setEditForm]     = useState(EMPTY_PARCEL_FORM);
  const [editCounties, setEditCounties] = useState([]);

  // Combine multiple parcels into one
  const [combineMode, setCombineMode] = useState(false);
  const [combineSel,  setCombineSel]  = useState(() => new Set());
  const [combineBusy, setCombineBusy] = useState(false);

  // Move a parcel to another R/E property
  const [moveParcelTarget, setMoveParcelTarget] = useState(null);
  const [moveTargets,      setMoveTargets]      = useState(null);
  const [moveTargetId,     setMoveTargetId]     = useState('');
  const [moveSaving,       setMoveSaving]       = useState(false);

  // ── County cascades ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!addForm.state) { setAddCounties([]); return; }
    fetchCountiesForState(addForm.state).then(setAddCounties);
  }, [addForm.state]);

  useEffect(() => {
    if (!editForm.state) { setEditCounties([]); return; }
    fetchCountiesForState(editForm.state).then(setEditCounties);
  }, [editForm.state]);

  // ── Enrich ───────────────────────────────────────────────────────────────
  const enrich = async (p) => {
    setEnriching(p.parcelID);
    try {
      await api.post(`parcels/${p.parcelID}/enrich`, {
        parcelNumber: p.parcelNumber || undefined,
        state:        p.state        || undefined,
        county:       p.county       || undefined,
        ppin:         p.ppin         || undefined,
      });
      reload();
    }
    catch (e) { toast.error(e.message); }
    finally { setEnriching(null); }
  };

  // ── Add / edit / delete ────────────────────────────────────────────────
  const addParcel = async () => {
    if (!addForm.state.trim() || !addForm.county.trim()) return;
    const effectiveDealDetailId = dealDetailId || undefined;
    if (!effectiveDealDetailId) { toast.error('No DealDetail context — cannot add parcel.'); return; }
    setSaving(true);
    try {
      const created = await api.post('parcels', {
        dealDetailId:    effectiveDealDetailId,
        parcelNumber:    addForm.parcelNumber    || null,
        taxParcelNumber: addForm.taxParcelNumber || null,
        regridParcelId:  addForm.regridParcelId  || null,
        state:           addForm.state           || null,
        county:          addForm.county          || null,
        ppin:            addForm.ppin            || null,
        acres:           addForm.acres           ? parseFloat(addForm.acres)           : null,
        legalDescription: addForm.legalDescription || null,
        purchasePrice:   addForm.purchasePrice   ? parseFloat(addForm.purchasePrice)   : null,
        salePrice:       addForm.salePrice       ? parseFloat(addForm.salePrice)       : null,
        purchasedDate:   addForm.purchasedDate   || null,
        soldDate:        addForm.soldDate        || null,
      });
      // Auto-enrich with Regrid if parcel identifier fields are available
      if (addForm.parcelNumber.trim() && addForm.state.trim() && addForm.county.trim()) {
        try {
          await api.post(`parcels/${created.parcelID}/enrich`, {
            parcelNumber: addForm.parcelNumber.trim(),
            state:        addForm.state.trim(),
            county:       addForm.county.trim(),
            ppin:         addForm.ppin || undefined,
          });
        } catch { /* enrich failure is non-fatal — parcel was still created */ }
      }
      setAddForm(EMPTY_PARCEL_FORM);
      setShowAdd(false);
      reload();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const startEdit = (p) => {
    setEditingId(p.parcelID);
    setEditForm({
      parcelNumber:    p.parcelNumber    || '',
      taxParcelNumber: p.taxParcelNumber || '',
      state:           p.state           || '',
      county:          p.county          || '',
      ppin:            p.ppin            || '',
      acres:           p.acres           != null ? String(p.acres) : '',
      legalDescription: p.legalDescription || '',
      notes:           p.notes           || '',
      purchasedDate:   p.purchasedDate   ? p.purchasedDate.slice(0,10) : '',
      soldDate:        p.soldDate        ? p.soldDate.slice(0,10)      : '',
      purchasePrice:   p.purchasePrice   != null ? String(p.purchasePrice) : '',
      salePrice:       p.salePrice       != null ? String(p.salePrice)     : '',
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`parcels/${editingId}`, {
        ...editForm,
        acres:         editForm.acres         ? parseFloat(editForm.acres)         : null,
        purchasePrice: editForm.purchasePrice  ? parseFloat(editForm.purchasePrice) : null,
        salePrice:     editForm.salePrice      ? parseFloat(editForm.salePrice)     : null,
        purchasedDate: editForm.purchasedDate  || null,
        soldDate:      editForm.soldDate       || null,
      });
      setEditingId(null);
      reload();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const deleteParcel = async (id) => {
    if (!window.confirm('Remove this parcel?')) return;
    try { await api.del(`parcels/${id}`); reload(); }
    catch (e) { toast.error(e.message); }
  };

  // ── Combine ────────────────────────────────────────────────────────────
  const toggleCombine = (parcelId) => setCombineSel(prev => {
    const next = new Set(prev);
    if (next.has(parcelId)) next.delete(parcelId); else next.add(parcelId);
    return next;
  });

  const runCombine = async () => {
    if (combineSel.size < 2 || !dealDetailId) return;
    setCombineBusy(true);
    try {
      const res = await api.post('parcels/combine', {
        dealDetailId, parcelIds: [...combineSel], mergedName: 'Combined parcel',
      });
      toast.success(`Combined ${res.mergedCount || combineSel.size} parcels into one.`);
      setCombineSel(new Set());
      setCombineMode(false);
      reload();
    } catch (e) { toast.error(e.message); }
    finally { setCombineBusy(false); }
  };

  // ── Move parcel to another property ─────────────────────────────────────
  const openMove = async (p) => {
    setMoveParcelTarget(p);
    setMoveTargetId('');
    setMoveTargets(null);
    try {
      const excludeId = p.dealDetailID || dealDetailId;
      const targets = await api.get(`parcel-move-targets${excludeId ? `?exclude=${excludeId}` : ''}`);
      setMoveTargets(targets || []);
    } catch (e) { toast.error(e.message); setMoveTargets([]); }
  };

  const confirmMove = async () => {
    if (!moveTargetId || !moveParcelTarget) return;
    setMoveSaving(true);
    try {
      await api.put(`parcels/${moveParcelTarget.parcelID}/move`, { targetDealDetailId: moveTargetId });
      setMoveParcelTarget(null);
      toast.success('Parcel moved');
      reload();
    } catch (e) { toast.error(e.message); }
    finally { setMoveSaving(false); }
  };

  // ── Render ────────────────────────────────────────────────────────────
  if (loading) return <div style={{ marginTop: 8 }}><Spinner /></div>;

  const list = parcels || [];

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: B.text }}>{list.length} Parcel{list.length !== 1 ? 's' : ''}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {dealDetailId && list.length >= 2 && (
            <Btn size="sm" variant="ghost" onClick={() => { setCombineMode(v => !v); setCombineSel(new Set()); }}>
              {combineMode ? 'Cancel Combine' : '⧉ Combine'}
            </Btn>
          )}
          {!dealId && (
            <Btn size="sm" onClick={() => setShowAdd(v => !v)}>{showAdd ? 'Cancel' : '+ Add Parcel'}</Btn>
          )}
        </div>
      </div>

      {/* Combine selection panel */}
      {combineMode && (
        <div style={{ background: B.surfaceMuted, border: `1px solid ${B.border}`, borderRadius: 8, padding: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: B.textMd, marginBottom: 8 }}>
            Select two or more parcels to dissolve into a single combined parcel (boundaries merged, acreage summed). The originals are archived.
          </div>
          <div style={{ maxHeight: 160, overflowY: 'auto', marginBottom: 10 }}>
            {list.map(p => (
              <label key={p.parcelID} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer', fontSize: 12, color: B.text }}>
                <input type="checkbox" checked={combineSel.has(p.parcelID)} onChange={() => toggleCombine(p.parcelID)} />
                <span style={{ flex: 1 }}>{p.parcelNumber || '(unnamed)'}</span>
                <span style={{ color: B.textLt }}>
                  {(p.regridAcres ?? p.acres) ? `${parseFloat(p.regridAcres ?? p.acres).toFixed(2)} ac` : '—'}
                </span>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Btn size="sm" disabled={combineSel.size < 2 || combineBusy} onClick={runCombine}>
              {combineBusy ? 'Combining…' : `Combine ${combineSel.size || ''} selected`}
            </Btn>
          </div>
        </div>
      )}

      {showAdd && (
        <div style={{ background: B.surfaceMuted, borderRadius: 8, padding: 14, marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 8 }}>
            <Input label="Parcel Number *" value={addForm.parcelNumber} onChange={v => setAddForm(p => ({ ...p, parcelNumber: v }))} />
            <Input label="County/Tax Office Parcel #" value={addForm.taxParcelNumber} onChange={v => setAddForm(p => ({ ...p, taxParcelNumber: v }))} />
            <Select label="State *" value={addForm.state} onChange={v => setAddForm(p => ({ ...p, state: v, county: '' }))}
              options={[{ value: '', label: '— Select State —' }, ...US_STATES]} />
            <Select label="County *" value={addForm.county} onChange={v => setAddForm(p => ({ ...p, county: v }))}
              disabled={!addForm.state}
              options={[{ value: '', label: addForm.state ? '— Select County —' : '— Pick state first —' }, ...addCounties.map(c => ({ value: c, label: c }))]} />
            <Input label="PPIN" value={addForm.ppin} onChange={v => setAddForm(p => ({ ...p, ppin: v }))} />
            <Input label="Acres" type="number" value={addForm.acres} onChange={v => setAddForm(p => ({ ...p, acres: v }))} />
            <Input label="Purchase Price" type="number" value={addForm.purchasePrice} onChange={v => setAddForm(p => ({ ...p, purchasePrice: v }))} />
            <Input label="Purchased Date" type="date" value={addForm.purchasedDate} onChange={v => setAddForm(p => ({ ...p, purchasedDate: v }))} />
            <Input label="Sold Date" type="date" value={addForm.soldDate} onChange={v => setAddForm(p => ({ ...p, soldDate: v }))} />
            <Input label="Sale Price" type="number" value={addForm.salePrice} onChange={v => setAddForm(p => ({ ...p, salePrice: v }))} />
          </div>
          <Input label="Legal Description" value={addForm.legalDescription} onChange={v => setAddForm(p => ({ ...p, legalDescription: v }))} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <Btn variant="ghost" size="sm" onClick={() => { setShowAdd(false); setAddForm(EMPTY_PARCEL_FORM); }}>Cancel</Btn>
            <Btn size="sm" disabled={!addForm.state || !addForm.county || saving} onClick={addParcel}>
              {saving ? 'Saving…' : 'Add Parcel'}
            </Btn>
          </div>
        </div>
      )}

      {list.length === 0 && !showAdd && (
        <div style={{ fontSize: 13, color: B.textLt, textAlign: 'center', padding: '20px 0' }}>
          {dealId
            ? 'This deal has no parcels yet. Add parcels on the deal’s real-estate detail — they’ll show up here for the farm.'
            : 'No parcels yet. Add a parcel then click Enrich to pull boundary data from Regrid, or use the Map tab to import from a KMZ boundary.'}
        </div>
      )}

      {list.map(p => (
        <div key={p.parcelID} style={{ borderBottom: `1px solid ${B.border}` }}>
          {editingId === p.parcelID ? (
            <div style={{ background: B.surfaceMuted, borderRadius: 8, padding: 14, margin: '8px 0' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 8 }}>
                <Input label="Parcel Number *" value={editForm.parcelNumber} onChange={v => setEditForm(f => ({ ...f, parcelNumber: v }))} />
                <Input label="County/Tax Office Parcel #" value={editForm.taxParcelNumber} onChange={v => setEditForm(f => ({ ...f, taxParcelNumber: v }))} />
                <Select label="State *" value={editForm.state} onChange={v => setEditForm(f => ({ ...f, state: v, county: '' }))}
                  options={[{ value: '', label: '— Select State —' }, ...US_STATES]} />
                <Select label="County *" value={editForm.county} onChange={v => setEditForm(f => ({ ...f, county: v }))}
                  disabled={!editForm.state}
                  options={[{ value: '', label: editForm.state ? '— Select County —' : '— Pick state first —' }, ...editCounties.map(c => ({ value: c, label: c }))]} />
                <Input label="PPIN" value={editForm.ppin} onChange={v => setEditForm(f => ({ ...f, ppin: v }))} />
                <Input label="Acres" type="number" value={editForm.acres} onChange={v => setEditForm(f => ({ ...f, acres: v }))} />
                <Input label="Purchase Price" type="number" value={editForm.purchasePrice} onChange={v => setEditForm(f => ({ ...f, purchasePrice: v }))} />
                <Input label="Purchased Date" type="date" value={editForm.purchasedDate} onChange={v => setEditForm(f => ({ ...f, purchasedDate: v }))} />
                <Input label="Sold Date" type="date" value={editForm.soldDate} onChange={v => setEditForm(f => ({ ...f, soldDate: v }))} />
                <Input label="Sale Price" type="number" value={editForm.salePrice} onChange={v => setEditForm(f => ({ ...f, salePrice: v }))} />
              </div>
              <Input label="Legal Description" value={editForm.legalDescription} onChange={v => setEditForm(f => ({ ...f, legalDescription: v }))} />
              <Input label="Notes" value={editForm.notes} onChange={v => setEditForm(f => ({ ...f, notes: v }))} />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <Btn variant="ghost" size="sm" onClick={() => setEditingId(null)}>Cancel</Btn>
                <Btn size="sm" disabled={saving} onClick={saveEdit}>{saving ? 'Saving…' : 'Save Changes'}</Btn>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 0' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>
                  {p.parcelNumber}
                  {p.taxParcelNumber && <span style={{ fontSize: 11, color: B.textLt, marginLeft: 6 }}>Tax #: {p.taxParcelNumber}</span>}
                  {p.ppin && <span style={{ fontSize: 11, color: B.textLt, marginLeft: 6 }}>PPIN: {p.ppin}</span>}
                </div>
                <div style={{ fontSize: 12, color: B.textMd, marginTop: 2 }}>
                  {[p.county, p.state].filter(Boolean).join(', ')}
                  {p.acres ? ` · ${parseFloat(p.acres).toFixed(2)} ac` : ''}
                  {p.regridAcres ? ` (Regrid: ${parseFloat(p.regridAcres).toFixed(2)} ac)` : ''}
                </div>
                {p.legalDescription && (
                  <div style={{ fontSize: 11, color: B.textMd, marginTop: 2, fontStyle: 'italic' }}>{p.legalDescription}</div>
                )}
                <div style={{ fontSize: 11, color: B.textMd, marginTop: 2, display: 'flex', gap: 12 }}>
                  {p.purchasedDate && <span>Purchased: {fmt.date(p.purchasedDate)}{p.purchasePrice ? ` · ${fmt.currency(p.purchasePrice)}` : ''}</span>}
                  {p.soldDate      && <span style={{ color: B.textLt }}>Sold: {fmt.date(p.soldDate)}{p.salePrice ? ` · ${fmt.currency(p.salePrice)}` : ''}</span>}
                </div>
                {p.regridEnrichedUtc && (
                  <div style={{ fontSize: 11, color: B.green, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}><Icon name="check" size={11} /> Enriched {fmt.date(p.regridEnrichedUtc)}</div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 10 }}>
                <Btn size="sm" variant="secondary" onClick={() => startEdit(p)}>Edit</Btn>
                <Btn size="sm" variant="secondary" disabled={enriching === p.parcelID} onClick={() => enrich(p)}>
                  {enriching === p.parcelID ? 'Enriching…' : 'Enrich'}
                </Btn>
                <Btn size="sm" variant="secondary" onClick={() => openMove(p)}>Move</Btn>
                <Btn size="sm" variant="danger" onClick={() => deleteParcel(p.parcelID)}>Remove</Btn>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Move parcel to another property */}
      {moveParcelTarget && (
        <Modal title="Move Parcel" width={440} onClose={() => setMoveParcelTarget(null)}>
          <div style={{ fontSize: 12, color: B.textMd, marginBottom: 16 }}>
            Move <strong>{moveParcelTarget.parcelNumber || 'this parcel'}</strong> to another real-estate property.
          </div>
          {moveTargets === null ? (
            <Spinner />
          ) : moveTargets.length === 0 ? (
            <div style={{ fontSize: 13, color: B.textLt, padding: '12px 0' }}>No other real-estate properties available to move to.</div>
          ) : (
            <Select label="Destination property" value={moveTargetId} onChange={setMoveTargetId}
              options={[
                { value: '', label: '— Select a property —' },
                ...moveTargets.map(t => ({ value: t.dealDetailID, label: `${t.dealName}${t.description ? ` — ${t.description}` : ''}` })),
              ]} />
          )}
          <ModalFooter>
            <Btn variant="ghost" onClick={() => setMoveParcelTarget(null)} disabled={moveSaving}>Cancel</Btn>
            <Btn onClick={confirmMove} disabled={!moveTargetId || moveSaving}>{moveSaving ? 'Moving…' : 'Move Parcel'}</Btn>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};
